/**
 * Reusable two-word split-title product section (NEWEST / PRODUCTS and
 * SEASON'S / TRENDING on the home page). The title slides apart on scroll
 * (IX2 a-49, scrubbed) and the grid renders product cards from the CMS.
 */
import type { FC } from 'react'
import type { Product } from '@/types'

import { useEffect, useRef } from 'react'
import Link from 'next/link'

import { gsap } from '@/lib/gsap'
import { ProductCard } from '@/components/common/product-card'

interface ProductSectionProps {
    id?: string
    containerClassName?: string
    titleLeft: string
    titleRight: string
    description: string
    products: Product[]
    descId: string
    buttonId: string
    viewAllHref?: string
}

export const ProductSection: FC<ProductSectionProps> = ({
    id,
    containerClassName = 'container',
    titleLeft,
    titleRight,
    description,
    products,
    descId,
    buttonId,
    viewAllHref = '/products'
}) => {
    const titleRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const title = titleRef.current
        if (!title) return

        const ctx = gsap.context(() => {
            const tl = gsap.timeline({
                scrollTrigger: { trigger: title, start: 'top bottom', end: 'bottom top', scrub: true }
            })

            tl.fromTo('.left-alighted-text', { xPercent: -10 }, { xPercent: 10, ease: 'none' }, 0)
                .fromTo('.right-alighted-text', { xPercent: 10 }, { xPercent: -10, ease: 'none' }, 0)
                .fromTo('.descripton-holder', { yPercent: 15 }, { yPercent: 0, ease: 'none' }, 0)
        }, title)

        return () => ctx.revert()
    }, [])

    return (
        <div className='section overflow-hidden' id={id}>
            <div className={containerClassName}>
                <div ref={titleRef} className='title-holder'>
                    <div className='left-alighted-text'>
                        <div className='fade-in-move-on-scroll'>
                            <h1 className='bold-text no-margins'>{titleLeft}</h1>
                        </div>
                    </div>
                    <div className='right-alighted-text'>
                        <div className='fade-in-move-on-scroll'>
                            <h1 className='bold-text no-margin---white'>{titleRight}</h1>
                        </div>
                    </div>
                    <div className='descripton-holder'>
                        <div className='w-layout-grid grid-6-col'>
                            <div id={descId} className='fade-in-move-on-scroll'>
                                <p>{description}</p>
                            </div>
                            <div id={buttonId} className='fade-in-move-on-scroll'>
                                <Link href={viewAllHref} className='button with-arrow w-button'>VIEW ALL</Link>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='season-trending-container'>
                    <div className='product-cards-wrapper w-dyn-list'>
                        <div role='list' className='product-grid w-dyn-items'>
                            {products.map((product) => (
                                <ProductCard key={product.id} product={product} />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
